import React, {FC, RefObject, useRef} from 'react';
import styled from 'styled-components';

import {useLocalize} from '../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {Localizer} from '../../../../../libs/shared/core/src/helpers/internationalization/localizeTypes';
import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {
  schedulingLinkMeetingDurationOptions,
  SchedulingLinkPropertiesEnum,
} from '../../../../helpers/models/schedulingLinkHelpers';
import {ConferenceTypesEnum} from '../../../../models/api/conferenceModel';
import {SchedulingLinkMeetingPropertiesModel} from '../../../../models/api/schedulingLinkModel';
import {SchedulingLinkQuestionModel} from '../../../../models/api/schedulingLinkQuestionModel';
import {HtmlEditorBoxHandle} from '../../../common/boxedInputs/htmlEditorBox';
import {HtmlEditorBoxContainer} from '../../../common/boxedInputs/htmlEditorBoxContainer';
import {ConferenceDropdownBoxContainer} from '../../editor/conferenceEditor/conferenceDropdownBoxContainer';
import {schedulingLinkPropertyPlaceholders} from '../schedulingLinkViewHelpers';
import {DurationDropdownBox} from './durationDropdownBox';
import {SchedulingLinkBoxedInputWrapper} from './schedulingLinkBoxedInputWrapper';
import {SchedulingLinkDetailRow} from './schedulingLinkDetailRow';
import {SchedulingLinkMeetingQuestions} from './schedulingLinkMeetingQuestions';
import {SchedulingLinkSettingsSection} from './schedulingLinkSettingsSection';

/*
 * Props.
 */

export interface SchedulingLinkMeetingSettingsProps {
  meetingProperties: SchedulingLinkMeetingPropertiesModel;
  supportedProviderConferenceTypes: ReadonlyArray<ConferenceTypesEnum>;
  onSummaryChange: (arg: string) => void;
  onDurationMinutesChange: (arg: number) => void;
  onConferenceChange: (arg: ConferenceTypesEnum | undefined) => void;
  onLocationChange: (arg: string) => void;
  onDescriptionChange: (arg: string) => void;
  onQuestionsChange: (arg: ReadonlyArray<SchedulingLinkQuestionModel>) => void;
  invalidQuestionOptionUids: ReadonlyArray<string>;
  setInvalidQuestionOptionUids: (arg: ReadonlyArray<string>) => void;
  schedulingCalendarVersionId: number;
  isEditing: boolean;
}

/*
 * Style.
 */

const StyledRowsWrapperDiv = styled.div`
  display: grid;
  grid-row-gap: 20px;
  padding-bottom: 20px;
`;

const StyledDropdownWrapperDiv = styled.div`
  width: 280px;
  > div {
    padding: 0px;
  }
`;

const StyledHtmlEditorBoxContainer = styled(HtmlEditorBoxContainer)`
  min-height: 70px;
  word-break: break-word;
`;

/*
 * Component.
 */

export const SchedulingLinkMeetingSettings: FC<SchedulingLinkMeetingSettingsProps> = (props) => {
  const localize = useLocalize();
  const editorRef = useRef<HtmlEditorBoxHandle>(null);
  return (
    <SchedulingLinkSettingsSection
      sectionSummary={
        <Trans id="schedulinglink-settings-meeting-summary">
          Customize the details of the calendar event that&apos;s created after a meeting is booked on this
          scheduling link.
        </Trans>
      }
    >
      <StyledRowsWrapperDiv>
        {renderTitleRow(localize, props)}
        {renderDurationRow(props)}
        {renderConferenceTypeRow(props)}
        {renderLocationRow(localize, props)}
        {renderDescriptionRow(localize, props, editorRef)}
        {renderQuestionRow(props)}
      </StyledRowsWrapperDiv>
    </SchedulingLinkSettingsSection>
  );
};

function renderTitleRow(localize: Localizer, props: SchedulingLinkMeetingSettingsProps) {
  return (
    <SchedulingLinkDetailRow label={<Trans id="schedulinglink-settings-meeting-title">Title</Trans>}>
      <SchedulingLinkBoxedInputWrapper>
        <TextBox
          placeholder={schedulingLinkPropertyPlaceholders[SchedulingLinkPropertiesEnum.TITLE](localize)}
          defaultValue={props.meetingProperties.summary}
          onChange={props.onSummaryChange}
        />
      </SchedulingLinkBoxedInputWrapper>
    </SchedulingLinkDetailRow>
  );
}

function renderDurationRow(props: SchedulingLinkMeetingSettingsProps) {
  return (
    <SchedulingLinkDetailRow label={<Trans id="schedulinglink-settings-duration">Duration</Trans>}>
      <StyledDropdownWrapperDiv>
        <DurationDropdownBox
          duration={props.meetingProperties.durationMinutes}
          onDurationSelect={props.onDurationMinutesChange}
          options={schedulingLinkMeetingDurationOptions}
          maxHeight={300}
        />
      </StyledDropdownWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderConferenceTypeRow(props: SchedulingLinkMeetingSettingsProps) {
  return (
    <SchedulingLinkDetailRow
      label={<Trans id="schedulinglink-settings-conference">Video conferencing</Trans>}
    >
      <StyledDropdownWrapperDiv>
        <ConferenceDropdownBoxContainer
          supportedProviderConferenceTypes={props.supportedProviderConferenceTypes}
          conferenceType={props.meetingProperties.conferenceType}
          onConferenceTypeChange={props.onConferenceChange}
          calendarVersionId={props.schedulingCalendarVersionId}
          isEditing={props.isEditing}
        />
      </StyledDropdownWrapperDiv>
    </SchedulingLinkDetailRow>
  );
}

function renderLocationRow(localize: Localizer, props: SchedulingLinkMeetingSettingsProps) {
  return (
    <SchedulingLinkDetailRow label={<Trans id="schedulinglink-settings-meeting-location">Location</Trans>}>
      <SchedulingLinkBoxedInputWrapper>
        <TextBox
          placeholder={schedulingLinkPropertyPlaceholders[SchedulingLinkPropertiesEnum.LOCATION](localize)}
          defaultValue={props.meetingProperties.location}
          onChange={props.onLocationChange}
        />
      </SchedulingLinkBoxedInputWrapper>
    </SchedulingLinkDetailRow>
  );
}

function renderDescriptionRow(
  localize: Localizer,
  props: SchedulingLinkMeetingSettingsProps,
  editorRef: RefObject<HtmlEditorBoxHandle>,
) {
  return (
    <SchedulingLinkDetailRow label={<Trans id="schedulinglink-settings-description">Description</Trans>}>
      <SchedulingLinkBoxedInputWrapper>
        <StyledHtmlEditorBoxContainer
          ref={editorRef}
          initialCleanHtml={props.meetingProperties.description}
          hasToolbar
          renderPlaceholder={() =>
            schedulingLinkPropertyPlaceholders[SchedulingLinkPropertiesEnum.DESCRIPTION](localize)
          }
          onChange={() => {
            const editor = editorRef.current;
            if (!editor) {
              return;
            }

            props.onDescriptionChange(!editor.isEmpty ? editor.cleanHtml : '');
          }}
        />
      </SchedulingLinkBoxedInputWrapper>
    </SchedulingLinkDetailRow>
  );
}

function renderQuestionRow(props: SchedulingLinkMeetingSettingsProps) {
  return (
    <SchedulingLinkDetailRow label={<Trans id="schedulinglink-settings-questions">Questions</Trans>}>
      <SchedulingLinkMeetingQuestions
        questions={props.meetingProperties.questions}
        onQuestionsChange={props.onQuestionsChange}
        invalidQuestionOptionUids={props.invalidQuestionOptionUids}
        setInvalidQuestionOptionUids={props.setInvalidQuestionOptionUids}
      />
    </SchedulingLinkDetailRow>
  );
}
